/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Container, Row, Col } from "react-awesome-styled-grid";
import { useHistory } from "react-router-dom";
import { Row as RowType } from "react-table";
import { Box, Heading, Loader, Modal } from "@nordcloud/gnui";
import styles from "~/assets/styles/CustomersList.module.css";
import { QueryPagination, Table } from "~/components";
import { TabsPath } from "~/components/Customer/CustomerNavigation";
import { useShowError } from "~/components/Error/useShowError";
import { ROUTES } from "~/routing";
import { useAxios, useQueryState } from "~/tools";
import useSortParams from "~/tools/hooks/useSortParams";
import { CustomerNavbar } from "./CustomerNavbar";
import { CustomersListColumns } from "./CustomersList";
import { createParameter } from "./utils";

export type CustomerRow = {
  id: string;
  name: string;
  categories: string[];
  providers: string[];
  premiumSupportCSPs: string[];
  klarityStatus: "CREATED" | "PENDING" | "CREATING_API_KEY_FAILED";
};

// eslint-disable-next-line sonarjs/cognitive-complexity
function HomePage() {
  const [customers, setCustomers] = React.useState([]);
  const [rows, setRows] = React.useState<Array<CustomerRow>>();
  const [isLoading, setIsLoading] = React.useState(true);
  const [
    { limit, page, category, provider, premiumSupportCSP, query },
  ] = useQueryState();
  const [totalCount, setTotalCount] = React.useState(0);
  const [pendingModalOpen, setPendingModalOpen] = React.useState(false);
  const [failedModalOpen, setFailedModalOpen] = React.useState(false);
  const [sortParams, setSorting] = useSortParams("name");

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const fetchCustomers = React.useCallback(async () => {
    const providers = createParameter(provider, "providers");
    const categories = createParameter(category, "categories");
    const premiumSupportCSPs = createParameter(
      premiumSupportCSP,
      "premiumSupportCSPs"
    );
    const url = `/customers?${providers}${categories}${premiumSupportCSPs}`;

    const params = { ...sortParams, limit, page, query };
    try {
      const { data } = await Axios().get(url, params);
      setTotalCount(data?.totalCount);
      setCustomers(data?.customers);
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [
    Axios,
    category,
    limit,
    page,
    provider,
    premiumSupportCSP,
    query,
    sortParams,
    showError,
  ]);

  React.useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  React.useEffect(() => {
    const rowsData: any =
      customers?.map((row: CustomerRow) => ({
        categories: row?.categories ?? [""],
        providers: row?.providers ?? "",
        name: row?.name ?? "",
        premiumSupportCSPs: row?.premiumSupportCSPs ?? [""],
        id: row?.id ?? "",
        klarityStatus: row?.klarityStatus ?? "CREATED",
      })) || [];
    setRows(rowsData);
  }, [customers]);

  const columns = React.useMemo(() => CustomersListColumns, []);

  const handleRowKeyPress = (
    e: React.KeyboardEvent<HTMLTableRowElement>,
    selected: CustomerRow
  ) => {
    if (e.key === "Enter") {
      openCustomer(selected);
    }
  };
  const onRedirectHandler = (row: CustomerRow) => {
    openCustomer(row);
  };

  const history = useHistory();
  const openCustomer = (customer: CustomerRow) => {
    if (customer.klarityStatus === "PENDING") {
      setPendingModalOpen(true);
    } else if (customer.klarityStatus === "CREATING_API_KEY_FAILED") {
      setFailedModalOpen(true);
    } else {
      history.push(`${ROUTES.customers.index}/${customer.id}/${TabsPath.INFO}`);
    }
  };

  const requestFormUrl =
    "https://mc.nordcloud.com/mc-automated-solutions-request";

  return (
    <Container>
      <Row>
        <Col>
          <Box spacing="spacing04">
            <Heading level={2}>List of Customers</Heading>
          </Box>

          <Box spacing="spacing04">
            <CustomerNavbar />
            {isLoading ? (
              <Loader inContent />
            ) : (
              <>
                <Table
                  getRowProps={(row: RowType<CustomerRow>) => ({
                    onClick: () => onRedirectHandler(row.original),
                    onKeyPress: (e: React.KeyboardEvent<HTMLTableRowElement>) =>
                      handleRowKeyPress(e, row.original),
                    className:
                      row.original.klarityStatus === "CREATED"
                        ? "interactive"
                        : `interactive ${styles.customerPending}`,
                    tabIndex: 0,
                    role: "button",
                  })}
                  columns={columns}
                  data={rows || []}
                  defaultSort={[{ id: "name", desc: false }]}
                  serverSideSort={setSorting}
                />
                <QueryPagination count={totalCount} />
              </>
            )}
          </Box>
        </Col>
      </Row>

      <Modal
        contentLabel="Customer pending"
        isOpen={pendingModalOpen}
        onClose={() => setPendingModalOpen(false)}
        actions={[]}
      >
        This customer is currently being created. It may take up to 5 minutes.
      </Modal>

      <Modal
        contentLabel="Customer failed to create"
        isOpen={failedModalOpen}
        onClose={() => setFailedModalOpen(false)}
        actions={[]}
      >
        Creating this customer has failed. We are currently investigating the
        error.
        <br />
        <br />
        If you want to request any additional help or information, please
        contact us using the&nbsp;
        <a href={requestFormUrl} target="_blank" rel="noreferrer">
          request form
        </a>
        .
      </Modal>
    </Container>
  );
}

export { HomePage };
